/**
 * sanitizes allocation data before sending to the API
 * converts empty strings to null for integer fields to support backspace = null UX
 */

import { TREATMENT_ALLOCATION_NAME_MAX_LENGTH } from "./allocation-constants";

type AllocationData = Record<string, unknown>;

const TREATMENT_INTEGER_FIELDS = ["units_per_year", "number_of_years"] as const;
const PRESCRIPTION_INTEGER_FIELDS = [
  "units_per_month",
  "number_of_years",
] as const;

/**
 * converts empty strings to null for integer fields
 * this ensures backspace in an integer field results in null (like a fresh row)
 */
function sanitizeIntegerFields(
  data: AllocationData,
  fields: readonly (keyof AllocationData)[],
): AllocationData {
  const sanitized = { ...data };

  fields.forEach((field) => {
    const val = sanitized[field];
    if (val === "" || val === undefined) {
      sanitized[field] = null;
    }
  });

  return sanitized;
}

function truncateTreatmentName(data: AllocationData): AllocationData {
  const name = data.name;
  if (
    typeof name !== "string" ||
    name.length <= TREATMENT_ALLOCATION_NAME_MAX_LENGTH
  ) {
    return data;
  }

  return {
    ...data,
    name: name.slice(0, TREATMENT_ALLOCATION_NAME_MAX_LENGTH),
  };
}

/**
 * sanitizes treatment allocation data before API submission
 */
export function sanitizeTreatmentAllocationData(
  data: AllocationData,
): AllocationData {
  return truncateTreatmentName(
    sanitizeIntegerFields(data, TREATMENT_INTEGER_FIELDS),
  );
}

/**
 * sanitizes prescription allocation data before API submission
 */
export function sanitizePrescriptionAllocationData(
  data: AllocationData,
): AllocationData {
  return sanitizeIntegerFields(data, PRESCRIPTION_INTEGER_FIELDS);
}
